export const routes = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Products', path: '/products' },
  { label: 'Process', path: '/process' },
  { label: 'Quality', path: '/quality' },
  { label: 'Global', path: '/global' },
  { label: 'Contact', path: '/contact' }
];

export const routeHref = (path) => (path === '/' ? '/' : `/#${path}`);

export const products = [
  {
    name: 'Fresh Coconuts',
    description: 'Carefully sourced fresh coconuts selected for natural freshness, consistent appearance, and export suitability.',
    image: 'https://images.pexels.com/photos/7522111/pexels-photo-7522111.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Naturally mature selection', 'Checked for appearance and condition', 'Suitable for wholesale and retail channels'],
    buyers: 'Importers, wholesalers, supermarkets, food service buyers, and distributors.',
    packaging: 'Packed according to product type, route, and buyer handling expectations.',
    quality: 'Selection focuses on freshness, maturity, size consistency, and shipment-readiness.'
  },
  {
    name: 'Semi-Husked Coconuts',
    description: 'Prepared for convenient handling and longer-distance movement, with cleaning and grading aligned to buyer requirements.',
    image: 'https://images.pexels.com/photos/19669106/pexels-photo-19669106.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Export-friendly preparation', 'Clean outer finish', 'Bulk movement suitability'],
    buyers: 'Wholesale buyers, processors, distribution networks, and import trading companies.',
    packaging: 'Bulk packing options can be discussed based on destination, volume, and logistics method.',
    quality: 'Each lot is reviewed for maturity, visible condition, and practical transport suitability.'
  },
  {
    name: 'Coconut Oil',
    description: 'Coconut oil supply conversations for food, wellness, and industrial buyers requiring dependable planning.',
    image: 'https://images.pexels.com/photos/725998/pexels-photo-725998.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Specification-led discussion', 'Volume planning support', 'Food and industrial relevance'],
    buyers: 'Food brands, ingredient buyers, wellness businesses, private-label teams, and industrial users.',
    packaging: 'Available packing should be discussed according to buyer specification, order size, and destination.',
    quality: 'Quality expectations are clarified before confirmation so supply aligns with the intended application.'
  },
  {
    name: 'Copra',
    description: 'Dried coconut kernel prepared for oil extraction, manufacturing, and ingredient supply chains.',
    image: 'https://images.pexels.com/photos/5608056/pexels-photo-5608056.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Oil extraction relevance', 'Manufacturing supply use', 'Bulk buyer suitability'],
    buyers: 'Oil mills, processors, manufacturers, and ingredient supply chains.',
    packaging: 'Packing is discussed around moisture expectations, transport duration, and buyer handling needs.',
    quality: 'Prepared lots are reviewed for dryness, visible condition, and suitability for processing use.'
  },
  {
    name: 'Coconut Powder',
    description: 'Fine coconut powder for bakeries, food manufacturers, confectionery teams, and private-label production.',
    image: 'https://images.pexels.com/photos/6682992/pexels-photo-6682992.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Ingredient-led supply', 'Bakery and confectionery use', 'Private-label relevance'],
    buyers: 'Bakeries, food manufacturers, confectionery companies, and ingredient distributors.',
    packaging: 'Packaging expectations are matched to handling, shelf movement, and buyer specification.',
    quality: 'Inquiries are aligned around texture, application, and product specification before supply planning.'
  },
  {
    name: 'Desiccated Coconut',
    description: 'Desiccated coconut supply for confectionery, bakery, ingredient, and food manufacturing requirements.',
    image: 'https://images.pexels.com/photos/8108249/pexels-photo-8108249.jpeg?auto=compress&cs=tinysrgb&w=1200',
    features: ['Food ingredient application', 'Bakery and confectionery relevance', 'Specification-based inquiries'],
    buyers: 'Ingredient importers, confectionery brands, bakeries, food manufacturers, and distributors.',
    packaging: 'Export packing can be discussed by grade, intended use, order size, and destination market.',
    quality: 'Buyer specifications guide discussions on texture, grade, packing, and handling expectations.'
  }
];

export const processSteps = [
  {
    number: '01',
    title: 'Farm Selection',
    description: 'Trusted sourcing networks are reviewed for product maturity, appearance, availability, and export suitability.'
  },
  {
    number: '02',
    title: 'Harvesting',
    description: 'Coconuts are planned around buyer timelines so freshness, maturity, and dispatch expectations stay aligned.'
  },
  {
    number: '03',
    title: 'Initial Inspection',
    description: 'Visible condition, size range, maturity, and product fit are checked before further handling.'
  },
  {
    number: '04',
    title: 'Cleaning',
    description: 'Products are cleaned and prepared with attention to handling hygiene and presentation for shipment.'
  },
  {
    number: '05',
    title: 'Grading & Sorting',
    description: 'Lots are sorted for consistency so buyers receive product that better matches the agreed requirement.'
  },
  {
    number: '06',
    title: 'Packaging',
    description: 'Packing is selected around product type, transit duration, buyer preference, and destination handling.'
  },
  {
    number: '07',
    title: 'Documentation',
    description: 'Commercial and shipment documents are organized to support smoother international movement.'
  },
  {
    number: '08',
    title: 'Container Loading',
    description: 'Loading coordination focuses on orderly movement, packing integrity, and dispatch visibility.'
  },
  {
    number: '09',
    title: 'Buyer Delivery Support',
    description: 'Clear updates continue through shipment coordination so buyers can plan receiving and distribution.'
  }
];

export const standards = [
  {
    title: 'Quality Checked at Every Stage',
    text: 'Selection, inspection, grading, packing, and dispatch are treated as connected checkpoints rather than isolated tasks.',
    proof: ['Visual condition review', 'Maturity and size discussion', 'Dispatch-readiness check']
  },
  {
    title: 'Graded for Consistency',
    text: 'Product lots are organized around buyer expectations so commercial supply is easier to receive, distribute, or process.',
    proof: ['Size and appearance grouping', 'Use-case alignment', 'Buyer specification review']
  },
  {
    title: 'Packed for Safe Movement',
    text: 'Packaging discussions consider product type, handling method, transit duration, and destination-market needs.',
    proof: ['Bulk handling readiness', 'Packing preference discussion', 'Route-aware preparation']
  },
  {
    title: 'Documentation Ready',
    text: 'Commercial paperwork and shipment information are prepared with attention to clarity and buyer communication.',
    proof: ['Invoice and packing detail support', 'Shipment milestone updates', 'Import-buyer coordination']
  },
  {
    title: 'Built for Bulk Buyers',
    text: 'The workflow is designed around real buyer questions: product fit, order volume, specification, packing, and timing.',
    proof: ['Specification-led inquiry flow', 'Volume planning support', 'Clear response process']
  }
];

export const brandValues = [
  {
    title: 'Quality-first sourcing',
    text: 'Product conversations begin with maturity, freshness, appearance, and destination suitability.'
  },
  {
    title: 'Transparent process',
    text: 'Buyers are guided through product selection, packaging, documentation, and dispatch expectations.'
  },
  {
    title: 'Reliable supply mindset',
    text: 'The focus stays on realistic supply planning, clear communication, and practical shipment coordination.'
  },
  {
    title: 'Long-term partnerships',
    text: 'Every inquiry is handled with the discipline needed for repeat wholesale and distribution relationships.'
  }
];

export const buyerTypes = [
  'Importers',
  'Wholesalers',
  'Distributors',
  'Supermarkets',
  'Hotels & restaurants',
  'Food processing businesses'
];

export const buyerSupport = [
  'Product and specification discussion before confirmation',
  'Packaging guidance based on destination and handling needs',
  'Shipment and documentation coordination for export movement',
  'Clear inquiry flow for bulk buyers and recurring supply planning'
];

export const markets = [
  {
    name: 'Middle East',
    text: 'High-volume trade conversations for wholesale, retail, hospitality, and food service buyers.'
  },
  {
    name: 'Europe',
    text: 'Specification-led discussions for importers, ingredient buyers, and distribution networks.'
  },
  {
    name: 'Southeast Asia',
    text: 'Regional buyer support for coconut products, processing needs, and distribution channels.'
  },
  {
    name: 'North America',
    text: 'Inquiry support for ingredient, retail, and specialty coconut product buyers.'
  }
];

export const marketCountries = [
  {
    id: 'uae',
    country: 'United Arab Emirates',
    region: 'Middle East',
    city: 'Dubai',
    image: '/media/markets/uae-dubai.jpg',
    x: 46,
    y: 39,
    summary: 'Importer, wholesale, hospitality, and retail supply conversations for coconut products moving into Gulf markets.',
    focus: ['Wholesale trade', 'Retail distribution', 'Hospitality supply']
  },
  {
    id: 'netherlands',
    country: 'Netherlands',
    region: 'Europe',
    city: 'Rotterdam',
    image: '/media/markets/netherlands-rotterdam.jpg',
    x: 41,
    y: 24,
    summary: 'Specification-led discussions for European importers, ingredient buyers, and distribution networks.',
    focus: ['Importer coordination', 'Ingredient supply', 'Distribution planning']
  },
  {
    id: 'singapore',
    country: 'Singapore',
    region: 'Southeast Asia',
    city: 'Singapore',
    image: '/media/markets/singapore.jpg',
    x: 68,
    y: 50,
    summary: 'Regional trade support for coconut products, processing needs, and destination-aware packing conversations.',
    focus: ['Regional buyers', 'Processing supply', 'Route-aware packing']
  },
  {
    id: 'united-states',
    country: 'United States',
    region: 'North America',
    city: 'New York',
    image: '/media/markets/united-states-new-york.jpg',
    x: 18,
    y: 30,
    summary: 'Inquiry support for specialty coconut products, retail channels, food service, and ingredient buyers.',
    focus: ['Specialty import', 'Food businesses', 'Retail channels']
  }
];
